import React, {FC, SyntheticEvent, useCallback, useLayoutEffect, useRef, useState} from "react";
import './HardCosts.less';
import {FormControl} from "@mui/material";
import DatePicker from "../../../ui/DateComponents/DatePicker/DatePicker";
import {CampaignHardCosts} from "../../../entities/campaign";
import {EditableTableCardProps} from "../../../ui/EditableTable/EditableTable";
import CurrencyInput from "../../../ui/Inputs/CurrencyInput";
import OkCancel from "../../../ui/Buttons/OkCancel";
import failureLadder from "../../../lib/failureLadder";
import {genUUID} from "../../../lib/picos/uuids";
import useComponentCache from "../../../hooks/useComponentCache";

export type HardCostsCardProps = EditableTableCardProps<CampaignHardCosts>;

type HardCostsCardState = {
    current: CampaignHardCosts,
    // True once the user has edited this cost and not yet submitted or
    // cancelled. The update gate (lib/updateGate.ts) reads it so a pending
    // client update never reloads over an unfinished cost (OWA-827, CXR-3-001).
    hasChanged: boolean,
}

const HardCostsCard: FC<HardCostsCardProps> = props => {
    const {cacheSave, cacheReset, cacheValue} = useComponentCache<HardCostsCardState>(JSON.stringify(props.current || 'hard-costs-card'));

    const [current, setCurrent] = useState<CampaignHardCosts>(cacheValue('current', props.current ?? {id: genUUID(), item: '', cost: 0, incurredOn: new Date().toUTCString()}));
    const [hasChanged, setHasChanged] = useState<boolean>(cacheValue('hasChanged', false));
    // After OK or Cancel the entry must stay gone: the unmount write below
    // would otherwise recreate it (with hasChanged) and the next new-cost
    // editor would restore a cost that was already submitted.
    const resetOnUnmount = useRef(false);

    // Save state
    useLayoutEffect(() => {
        return () => {
            if (resetOnUnmount.current) cacheReset();
            else cacheSave({current, hasChanged});
        };
    }, [cacheReset, cacheSave, current, hasChanged]);

    const a = current.item === '';
    const b = current.cost === 0;

    const markChanged = useCallback(() => {
        resetOnUnmount.current = false;
        setHasChanged(true);
    }, []);

    const handleIncurredChange = useCallback((date: Date) => {
        setCurrent({...current, incurredOn: date.toUTCString()});
        markChanged();
    }, [current, setCurrent, markChanged]);

    const handleItemBlur = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setCurrent({...current, item: e.target.value});
        markChanged();
    }, [current, setCurrent, markChanged]);

    const handleCostBlur = useCallback((cost: number) => {
        setCurrent({...current, cost});
        markChanged();
    }, [current, setCurrent, markChanged]);

    const onOk = (e: SyntheticEvent) => {
        e.preventDefault();

        resetOnUnmount.current = true;
        cacheReset();
        setHasChanged(false);
        props.onOk(current);
    }

    const onCancel = (e: SyntheticEvent) => {
        e.preventDefault();

        resetOnUnmount.current = true;
        cacheReset();
        setHasChanged(false);
        props.onCancel();
    }

    const disabled = current.cost <= 0 && current.item.trim() === '' && current.incurredOn === '';

    const failureReasons = failureLadder([
        [current.cost <= 0, 'Cost must be greater than zero'],
        [current.item.trim() === '', 'Item cannot be blank'],
        [current.incurredOn === '', 'Date cannot be blank'],
    ]);

    return <div className='hard-costs__line Form'>
        {/* Incurred Date */}
        <FormControl variant='standard' sx={{display: 'flex'}}
        >
            <label htmlFor={`hardCosts-${current.id}-IncurredOn`}>Incurred On</label>
            <div id={`hardCosts-${current.id}-IncurredOn`}>
            <DatePicker name='hardCosts'
                        defaultValue={current.incurredOn}
                        autoFocus={a && b}
                        useTime={false}
                        onChange={handleIncurredChange}
            />
            </div>
        </FormControl>

        {/* Item Name */}
        <FormControl id={`hardCosts-${current.id}-Item`}>
            <label htmlFor={`hardCosts-${current.item}-Item`}>Description</label>
            <input id={`hardCosts-${current.item}-Item`}
                   key={`hardCosts-${current.item}-Item`}
                   autoFocus={!a && b}
                   type='text'
                   defaultValue={current.item}
                   onBlur={handleItemBlur}
                   placeholder='What was done?'
                   style={{height: '2.5em' }}
            />
        </FormControl>

        {/* Per-Item Cost */}
        <FormControl id={`hardCosts-${current.id}-Cost`} sx={{width: '8em'}}>
            <label htmlFor={current.id}>Cost</label>
            <CurrencyInput id={current.id}
                           value={Number(current.cost)}
                           currency='USD'
                           useCommas={true}
                           useEuropean={false}
                           onBlur={handleCostBlur}
            />
        </FormControl>

        <OkCancel label='Hard Cost'
                  disabled={disabled}
                  disabledMessage={failureReasons}
                  onClick={onOk}
                  onSubmit={onOk}
                  onCancel={onCancel}
                  onTop={true}
        />
    </div>;
}

export default HardCostsCard;